import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import { FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { message } from "antd";
import dayjs from "dayjs";

export const useProductAvailability = (dateRange, products = []) => {
  const [loading, setLoading] = useState(true);
  const [availabilityData, setAvailabilityData] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1] || products.length === 0) {
      setLoading(false);
      return;
    }

    fetchAvailabilityData();
  }, [dateRange, products]);

  const fetchAvailabilityData = async () => {
    setLoading(true);
    setError(null);

    try {
      const db = firestore;
      const startDate = dayjs(dateRange[0]).format("DDMMYYYY");
      const endDate = dayjs(dateRange[1]).format("DDMMYYYY");

      // Fetch dailyStatus for all products in parallel
      const fetchPromises = products.map(async (product) => {
        const productId = product.id || product.productId;

        try {
          const dailyStatusQuery = query(
            collection(
              db,
              `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/productAvailabilityLogs/${productId}/dailyStatus`,
            ),
            where("date", ">=", startDate),
            where("date", "<=", endDate),
          );

          const snapshot = await getDocs(dailyStatusQuery);

          const dailyStatuses = snapshot.docs
            .map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }))
            .sort((a, b) => a.date.localeCompare(b.date));

          return {
            productId,
            data: {
              productId,
              productName: product.productName || product.productNameNL || "Unknown",
              dailyStatuses,
            },
          };
        } catch (err) {
          console.error(`Error fetching availability for product ${productId}:`, err);
          return null;
        }
      });

      const results = await Promise.all(fetchPromises);

      // Build availabilityMap from results
      const availabilityMap = {};
      results.forEach((result) => {
        if (result) {
          availabilityMap[result.productId] = result.data;
        }
      });

      setAvailabilityData(availabilityMap);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching product availability:", err);
      setError(err.message);
      setLoading(false);
      message.error("Failed to fetch product availability data");
    }
  };

  /**
   * Get availability timeline for a specific product
   * Combines all dailyStatus timelines into one continuous timeline
   */
  const getProductTimeline = (productId) => {
    const productData = availabilityData[productId];
    if (!productData || !productData.dailyStatuses) {
      return [];
    }

    // Combine all statusTimeline arrays from all days
    const fullTimeline = [];
    productData.dailyStatuses.forEach((dailyStatus) => {
      if (dailyStatus.statusTimeline) {
        fullTimeline.push(...dailyStatus.statusTimeline);
      }
    });

    return fullTimeline;
  };

  /**
   * Calculate availability percentage for a product in the date range
   */
  const getAvailabilityPercentage = (productId) => {
    const timeline = getProductTimeline(productId);
    if (timeline.length === 0) return 0;

    const availableCount = timeline.filter((status) => status.isAvailable).length;
    return Math.round((availableCount / timeline.length) * 100);
  };

  /**
   * Get unavailable periods for a product
   * Returns array of periods where product was not available with reasons
   */
  const getUnavailablePeriods = (productId, filters) => {
    const timeline = getProductTimeline(productId);
    if (timeline.length === 0) return [];

    const periods = [];
    let currentPeriod = null;

    timeline.forEach((status, index) => {
      const isAvailable = calculateFilteredAvailability(status, filters);
      const nextStatus = timeline[index + 1];

      if (!isAvailable) {
        const reasons = getUnavailabilityReasons(status, filters);

        // Determine end timestamp: use next status timestamp or current if last
        const endTimestamp = nextStatus ? nextStatus.timestamp : status.timestamp;

        if (currentPeriod && currentPeriod.reasons.join(",") === reasons.join(",")) {
          // Extend current period
          currentPeriod.endTimestamp = endTimestamp;
        } else {
          // Start new period
          if (currentPeriod) {
            periods.push(currentPeriod);
          }
          currentPeriod = {
            startTimestamp: status.timestamp,
            endTimestamp: endTimestamp,
            reasons: reasons,
          };
        }
      } else {
        // Available - close current period if exists
        if (currentPeriod) {
          periods.push(currentPeriod);
          currentPeriod = null;
        }
      }
    });

    // Close last period if still open
    if (currentPeriod) {
      periods.push(currentPeriod);
    }

    return periods;
  };

  /**
   * Calculate availability based on selected filters
   */
  const calculateFilteredAvailability = (status, filters) => {
    const checks = [];

    if (filters.productInStock) {
      checks.push(status.productInStock);
    }
    if (filters.productInSeason) {
      checks.push(status.productInSeason);
    }
    if (filters.isVisible) {
      checks.push(status.isVisible);
    }
    if (filters.stockLevel) {
      checks.push(status.stockLevel > 0);
    }

    // If no filters selected, consider available (no restrictions)
    if (checks.length === 0) return true;

    // All selected checks must pass
    return checks.every((check) => check === true);
  };

  /**
   * Get reasons why product is unavailable
   */
  const getUnavailabilityReasons = (status, filters) => {
    const reasons = [];

    if (filters.productInStock && !status.productInStock) {
      reasons.push("out_of_stock");
    }
    if (filters.productInSeason && !status.productInSeason) {
      reasons.push("out_of_season");
    }
    if (filters.isVisible && !status.isVisible) {
      reasons.push("hidden");
    }
    if (filters.stockLevel && status.stockLevel <= 0) {
      reasons.push("no_stock_available");
    }

    return reasons;
  };

  return {
    loading,
    error,
    availabilityData,
    getProductTimeline,
    getAvailabilityPercentage,
    getUnavailablePeriods,
    getUnavailabilityReasons,
    calculateFilteredAvailability,
  };
};
